import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import Button from '@/components/button'
import DataTable from '@/components/data-table'
import type { Column } from '@/components/data-table'
import FilterBar from '@/components/filter-bar'
import FormField from '@/components/form-field'
import LookupSelect from '@/components/lookup-select'
import PageHeader from '@/components/page-header'
import StatusBadge from '@/components/status-badge'
import { useOperadorasLookupQuery } from '@/http/queries/lookups'
import { useTiposCredenciaisQuery } from '@/http/queries/tipos-credenciais'
import type { LookupItem } from '@/types/lookup'
import type { TipoCredencialResumo } from '@/types/tipo-credencial'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import { orDash } from '@/utility/Utils'
import styles from './style.module.css'

const TiposCredenciaisList = () => {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [busca, setBusca] = useState('')
  const [buscaAplicada, setBuscaAplicada] = useState('')
  const [operadoraEscolhida, setOperadoraEscolhida] = useState<LookupItem | null>(null)
  const [operadoraAplicada, setOperadoraAplicada] = useState<LookupItem | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [termoOperadora, setTermoOperadora] = useState('')

  const buscaDebounced = useDebouncedValue(busca)
  const termoOperadoraDebounced = useDebouncedValue(termoOperadora)

  const operadoras = useOperadorasLookupQuery(
    termoOperadoraDebounced,
    termoOperadoraDebounced.trim() !== '',
  )

  useEffect(() => {
    setBuscaAplicada(buscaDebounced)
    setPageNumber(1)
  }, [buscaDebounced])

  const filtro = useMemo(
    () => ({
      busca: buscaAplicada || null,
      operadoraId: operadoraAplicada?.id ?? null,
      pageNumber,
      pageSize,
    }),
    [buscaAplicada, operadoraAplicada, pageNumber, pageSize],
  )

  const { data: page, error, isFetching, refetch } = useTiposCredenciaisQuery(filtro)

  const limparFiltros = () => {
    setBusca('')
    setBuscaAplicada('')
    setTermoOperadora('')
    setOperadoraEscolhida(null)
    setOperadoraAplicada(null)
    setPageNumber(1)
  }

  const columns: Column<TipoCredencialResumo>[] = [
    {
      key: 'nome',
      header: 'Tipo de credencial',
      render: (tipo) => (
        <div className={styles.stack}>
          <strong>{orDash(tipo.nomeAmigavel)}</strong>
          <span className={styles.muted}>Código {orDash(tipo.codigoOperadora)}</span>
        </div>
      ),
    },
    {
      key: 'operadora',
      header: 'Operadora',
      render: (tipo) => (
        <div className={styles.stack}>
          <span>{orDash(tipo.operadora)}</span>
          <span className={styles.muted}>ANS {orDash(tipo.registroAns)}</span>
        </div>
      ),
    },
    { key: 'interface', header: 'Interface', render: (tipo) => orDash(tipo.interfaceAutorizacao) },
    {
      key: 'credenciais',
      header: 'Credenciais',
      render: (tipo) => `${tipo.quantidadeCredenciais}`,
    },
    {
      key: 'procedimentos',
      header: 'Procedimentos por guia',
      render: (tipo) =>
        tipo.limitOfProceduresPerRequest && tipo.limitOfProceduresPerRequest > 0
          ? `${tipo.limitOfProceduresPerRequest}`
          : 'Sem limite',
    },
    {
      key: 'recursos',
      header: 'Recursos',
      render: (tipo) => (
        <span className={styles.badges}>
          <StatusBadge
            label="Elegibilidade"
            tone={tipo.hasEligibility ? 'success' : 'neutral'}
          />
          <StatusBadge
            label="Cancelamento"
            tone={tipo.hasCancellation ? 'success' : 'neutral'}
          />
        </span>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Tipos de credencial"
        subtitle="Configuração que as credenciais herdam: campos exigidos, limites e qual crawler atende"
        actions={
          can(ACAO.EditarTipoCredencial) ? (
            <Button iconLeft={<Plus size={14} />} onClick={() => navigate('/tipos-credenciais/novo')}>
              Novo tipo
            </Button>
          ) : null
        }
      />

      <FilterBar
        searchValue={busca}
        onSearchChange={setBusca}
        onSearchClear={() => setBusca('')}
        searchPlaceholder="Buscar por nome, código ou operadora"
        searchLabel="Buscar tipos de credencial"
        isSearching={isFetching && busca !== buscaDebounced}
        chips={
          operadoraAplicada
            ? [
                {
                  key: 'operadoraId',
                  label: 'Operadora',
                  value: operadoraAplicada.rotulo,
                  onRemove: () => {
                    setTermoOperadora('')
                    setOperadoraEscolhida(null)
                    setOperadoraAplicada(null)
                    setPageNumber(1)
                  },
                },
              ]
            : []
        }
        activeCount={operadoraAplicada ? 1 : 0}
        onApply={() => {
          setOperadoraAplicada(operadoraEscolhida)
          setPageNumber(1)
        }}
        onClear={limparFiltros}
        advanced={
          <FormField label="Operadora" htmlFor="operadoraIdTipo">
            <LookupSelect
              id="operadoraIdTipo"
              termo={termoOperadora}
              onTermoChange={setTermoOperadora}
              itens={operadoras.data ?? []}
              isLoading={operadoras.isFetching}
              selecionado={operadoraEscolhida}
              placeholder="Preencha o nome ou o registro ANS"
              onSelecionar={setOperadoraEscolhida}
            />
          </FormField>
        }
      />

      <DataTable
        columns={columns}
        onRowClick={(tipo) => navigate(`/tipos-credenciais/${tipo.id}`)}
        page={page}
        getRowKey={(tipo) => tipo.id}
        isLoading={isFetching}
        error={error}
        onRetry={() => void refetch()}
        onPageChange={setPageNumber}
        pageSize={pageSize}
        onPageSizeChange={(novoTamanho) => {
          setPageSize(novoTamanho)
          setPageNumber(1)
        }}
        emptyTitle="Nenhum tipo de credencial encontrado"
        emptyDescription="Ajuste os filtros e tente novamente."
      />
    </>
  )
}

export default TiposCredenciaisList
